import { doc, getDoc, getDocs, collection, writeBatch, newId } from '@battleops/localstore';
import { buildTasksFromSteps, automationRoutine, normalizeRoutine, nextSlot, localDateStr, commands } from '@battleops/shared';
import { commitWrites } from './commit';
import { currentUid } from '../session/boot';
import { db } from './store';
import { makeTaskDoc } from './taskDoc';
import { fireRoutineEvent } from './routineEvents';

// Run a process: its steps become tasks, the run is logged, the process counts it — one batch, so
// a half-run never syncs. Step dependencies become blockedByTaskId (blocked until the step before).
export async function runProcess(process, scheduledDate, triggerType, triggerDetail = null, triggerContext = null) {
  const templates = buildTasksFromSteps(process, scheduledDate, triggerContext?.projectId || null);
  const stepToTask = {};
  const batch = writeBatch(db);
  const created = [];
  const now = Date.now();
  templates.forEach((tpl, i) => {
    const id = newId();
    stepToTask[tpl._stepId] = id;
    const blockedByTaskId = tpl._blockedBy ? stepToTask[tpl._blockedBy] || null : null;
    const t = makeTaskDoc({
      id, title: tpl.title, description: tpl.description, priority: tpl.priority, projectId: tpl.projectId,
      scheduledDate: tpl.scheduledDate, timeBlock: tpl.timeBlock, estimatedMinutes: tpl.estimatedMinutes,
      taskType: tpl.taskType, billable: tpl.billable, status: blockedByTaskId ? 'blocked' : 'todo',
      processId: process.id, processStepId: tpl._stepId, blockedByTaskId, sortOrder: (i + 1) * 1000 + now % 1000,
    }, [], now + i);
    batch.set(doc(db, 'tasks', id), t);
    created.push({ taskId: id, stepId: tpl._stepId, title: tpl.title });
  });
  const execId = newId();
  batch.set(doc(db, 'processExecutions', execId), {
    id: execId, processId: process.id, processName: process.name, triggerType, triggerDetail, triggerContext,
    executedAt: now, scheduledDate, tasksCreated: created, status: 'completed', error: null,
  });
  batch.update(doc(db, 'processes', process.id), { lastExecutedAt: now, executionCount: (process.executionCount || 0) + 1 });
  await batch.commit();
  return created;
}

// clock_in / clock_out / task_complete → routines listening for it (data/routineEvents.js). The name stays for
// TaskContext's call sites. Fire-and-forget: a routine failing must never break the clock or a completion.
export function fireProcessEvent(event, eventDetail = {}) {
  const type = event === 'task_complete' ? 'task_done' : event;
  const at = Date.now();
  const p = fireRoutineEvent({ type, projectId: eventDetail.projectId || null, taskId: eventDetail.taskId || null, at });
  if ((type === 'clock_in' || type === 'clock_out') && eventDetail.projectId) return p.then((made) => startProjectRoutine(type, eventDetail.projectId, at, made));
  return p;
}

// Project automations (v0.1.247, shared/processes.js): "when I clock in here → start routine R" / clock out. Today's run
// is made now (due at the event) — the reminder pipeline rings it like any event-made run. Skipped when the routine's
// own trigger already made one this moment, or it has no free slot today.
async function startProjectRoutine(type, projectId, at, made = []) {
  try {
    const snap = await getDoc(doc(db, 'projects', projectId));
    const routineId = snap.exists() ? automationRoutine({ id: projectId, ...snap.data() }, type) : null;
    if (!routineId || made.some((r) => r.routineId === routineId)) return made;
    const raw = (await getDocs(collection(db, 'processes'))).docs.map((d) => ({ id: d.id, ...d.data() }));
    const runs = (await getDocs(collection(db, 'processExecutions'))).docs.map((d) => ({ id: d.id, ...d.data() }));
    const r = normalizeRoutine(raw.find((x) => x.id === routineId));
    if (!r || !r.steps?.length) return made;
    const date = localDateStr(at);
    const uid = currentUid();
    const n = nextSlot(r, runs, date, uid);
    if (!n) return made;
    const res = commands.materializeRun({ routines: raw, runs, uid, now: at, newId }, { routineId, date, n, dueAt: at, trigger: { type, at, projectId } });
    if (res.created) { await commitWrites(res.writes); return [...made, res.run]; }
  } catch (e) { console.error('project routine failed:', e); }
  return made;
}
